import AIButton from '@cobliteam/ai/src/components/AIButton/AIButton';
import { useLanguage, useTranslation } from '@cobliteam/i18n';
import Badge from '@cobliteam/ui/components/Badge';
import Button from '@cobliteam/ui/components/Button';
import Popover, { PopoverContent, PopoverTrigger } from '@cobliteam/ui/components/Popover';
import Tabs, { TabsList, TabsTrigger } from '@cobliteam/ui/components/Tabs';
import Tooltip from '@cobliteam/ui/components/Tooltip';
import Typography from '@cobliteam/ui/components/Typography';
import Icon from '@cobliteam/ui/icons';
import { cn } from '@cobliteam/ui/utils';
import {
  type ActionButton,
  type ButtonSetContentBlock,
  type FeedClientOptions,
  type FeedItem,
  type MarkdownContentBlock,
  NetworkStatus,
  type TextContentBlock,
} from '@knocklabs/client';
import { useKnockFeed } from '@knocklabs/react';
import DOMPurify from 'dompurify';
import { type MouseEvent, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';

/**
 * Sino do feed in-app do header, com backend no Knock.
 *
 * Usa a camada headless do `@knocklabs/react`: o `KnockFeedProvider` continua
 * fornecendo o feed client/store, mas a UI é renderizada com o design system da Cobli.
 */
type FeedTab = 'all' | 'unread' | 'archived';
type BlockByName = Record<string, FeedItem['blocks'][number]>;

const getFeedOptionsForTab = (tab: FeedTab): FeedClientOptions => {
  if (tab === 'archived') return { status: 'all', archived: 'only' };
  return { status: tab === 'unread' ? 'unread' : 'all', archived: 'exclude' };
};

const maybeNavigateToUrlWithDelay = (url?: string) => {
  if (!url) return;
  // Adia a navegação para dar tempo do `markAsInteracted` (disparado logo antes do clique)
  // ser enviado ao Knock. `window.location.assign` é síncrono e destrói o contexto JS na hora,
  // cancelando a request de interação em voo; o atraso garante essa ordem (interação → redirect).
  setTimeout(() => window.location.assign(url), 200);
};

const getBlocksByName = (item: FeedItem): BlockByName =>
  item.blocks.reduce<BlockByName>((acc, block) => {
    acc[block.name] = block;
    return acc;
  }, {});

const getActionUrl = (blocksByName: BlockByName) => (blocksByName.action_url as TextContentBlock | undefined)?.rendered;

const getBodyHtml = (blocksByName: BlockByName) => (blocksByName.body as MarkdownContentBlock | TextContentBlock | undefined)?.rendered;

const formatInsertedAt = (insertedAt: string, locale: string) =>
  new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    month: 'short',
  }).format(new Date(insertedAt));

type KnockFeedItemProps = {
  item: FeedItem;
  isArchivedView: boolean;
  locale: string;
  onArchive: (item: FeedItem) => void;
  onButtonAction: (item: FeedItem, action: ActionButton) => void;
  onItemClick: (item: FeedItem, actionUrl?: string) => void;
  onUnarchive: (item: FeedItem) => void;
};

const KnockFeedItem = ({ item, isArchivedView, locale, onArchive, onButtonAction, onItemClick, onUnarchive }: KnockFeedItemProps) => {
  const { t } = useTranslation('app-header');
  const blocksByName = useMemo(() => getBlocksByName(item), [item]);
  const rawBodyHtml = getBodyHtml(blocksByName);
  const actionUrl = getActionUrl(blocksByName);
  const buttonSet = blocksByName.actions as ButtonSetContentBlock | undefined;
  const isUnread = !item.read_at;

  const handleItemClick = () => onItemClick(item, actionUrl);

  const handleArchiveClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (isArchivedView) {
      onUnarchive(item);
      return;
    }

    onArchive(item);
  };

  return (
    <div className={cn('group flex gap-3 border-gray-5 border-b px-4 py-3 transition-colors focus-within:bg-gray-2 hover:bg-gray-2', isUnread && 'bg-primary-1')}>
      <span className={cn('mt-1.5 size-2 shrink-0 rounded-full', isUnread ? 'bg-primary' : 'bg-transparent')} aria-hidden />

      <div className='min-w-0 flex-1'>
        <button type='button' className='block w-full rounded-sm text-left outline-none focus-visible:ring-2 focus-visible:ring-primary-7' onClick={handleItemClick}>
          {rawBodyHtml ? (
            <span
              className='prose-p:my-0 block max-w-none prose-a:text-primary text-gray-12 text-sm leading-5 prose-a:underline [&_*]:break-words [&_*]:[overflow-wrap:anywhere]'
              // biome-ignore lint/security/noDangerouslySetInnerHtml: Knock feed body HTML is sanitized with DOMPurify before injection.
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(rawBodyHtml) }}
            />
          ) : (
            <Typography variant='text-sm' color='secondary'>
              {t('feed.item.no_content')}
            </Typography>
          )}

          <span className='mt-2 flex items-center gap-1.5'>
            <Typography as='span' variant='caption-xs' color='secondary'>
              {formatInsertedAt(item.inserted_at, locale)}
            </Typography>
            {isUnread && (
              <>
                <span className='size-0.5 shrink-0 rounded-full bg-gray-8' aria-hidden />
                <Typography as='span' variant='caption-xs-semibold' className='text-primary'>
                  {t('feed.item.unread')}
                </Typography>
              </>
            )}
          </span>
        </button>

        {buttonSet?.buttons.length ? (
          <div className='mt-3 flex flex-wrap gap-2'>
            {buttonSet.buttons.map((button, index) => (
              <Button
                key={button.name}
                type='button'
                size='sm'
                variant={index === 0 ? 'outlined' : 'ghost'}
                color={index === 0 ? 'primary' : 'default'}
                onClick={(event) => {
                  event.stopPropagation();
                  onButtonAction(item, button);
                }}
              >
                {button.label}
              </Button>
            ))}
          </div>
        ) : null}
      </div>

      <Tooltip content={isArchivedView ? t('feed.actions.unarchive') : t('feed.actions.archive')} asChild>
        <Button
          type='button'
          size='sm'
          variant='ghost'
          color='default'
          icon={<Icon name={isArchivedView ? 'archive-restore' : 'archive'} />}
          aria-label={isArchivedView ? t('feed.actions.unarchive') : t('feed.actions.archive')}
          className='shrink-0 text-gray-10 opacity-70 transition-opacity hover:opacity-100 focus-visible:opacity-100'
          onClick={handleArchiveClick}
        />
      </Tooltip>
    </div>
  );
};

const KnockFeedButton = () => {
  const { t } = useTranslation('app-header');
  const { currentLanguage } = useLanguage();
  const { feedClient, useFeedStore } = useKnockFeed();
  const history = useHistory();
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<FeedTab>('all');
  const items = useFeedStore((state) => state.items);
  const metadata = useFeedStore((state) => state.metadata);
  const networkStatus = useFeedStore((state) => state.networkStatus);
  const pageInfo = useFeedStore((state) => state.pageInfo);

  const isLoading = networkStatus === NetworkStatus.loading;
  const isFetchingMore = networkStatus === NetworkStatus.fetchMore;
  const isError = networkStatus === NetworkStatus.error;
  const isArchivedView = activeTab === 'archived';
  const unreadCount = metadata.unread_count;
  const unseenCount = metadata.unseen_count;
  // Badge mostra a quantidade de não lidas; vira um dot quando todas já foram vistas (mas seguem não lidas).
  const hasUnread = unreadCount > 0;
  const showUnreadDot = hasUnread && unseenCount === 0;

  useEffect(() => {
    if (!isOpen) return;
    void feedClient.fetch(getFeedOptionsForTab(activeTab));
  }, [activeTab, feedClient, isOpen]);

  useEffect(() => {
    if (!(isOpen && metadata.unseen_count > 0)) return;
    void feedClient.markAllAsSeen();
  }, [feedClient, isOpen, metadata.unseen_count]);

  // Resolve a `action` do Knock pelo formato:
  // - começa com `?` → query params: atualiza só a search da rota atual pelo router (sem reload).
  // - URL http(s) completa → redirect de página inteira (delay garante interação → redirect).
  // - caminho relativo (`/algo`) → navega pelo router, sem reload.
  // Em deep links que abrem um drawer (param com sentinela `true`), troca o valor pelo id do item do
  // feed, pra superfície destino resolver e exibir o evento certo.
  const navigateToAction = (url: string | undefined, item: FeedItem) => {
    if (!url) return;
    if (url.startsWith('?')) {
      const params = new URLSearchParams(url);
      for (const [key, value] of params) {
        if (value === 'true') params.set(key, item.id);
      }
      history.push({ search: `?${params.toString()}` });
      return;
    }
    if (/^https?:\/\//.test(url)) {
      maybeNavigateToUrlWithDelay(url);
      return;
    }
    history.push(url);
  };

  const handleItemClick = (item: FeedItem, actionUrl?: string) => {
    void feedClient.markAsInteracted(item, {
      action: actionUrl ?? '',
      type: 'cell_click',
    });
    setIsOpen(false);
    navigateToAction(actionUrl, item);
  };

  const handleButtonAction = (item: FeedItem, action: ActionButton) => {
    void feedClient.markAsInteracted(item, {
      action: action.action,
      label: action.label,
      name: action.name,
      type: 'button_click',
    });
    setIsOpen(false);
    navigateToAction(action.action, item);
  };

  const handleLoadMore = () => {
    void feedClient.fetchNextPage(getFeedOptionsForTab(activeTab));
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <Tooltip content={t('feed.tooltip')} position='right' asChild>
        <PopoverTrigger asChild>
          <div className='relative'>
            <Badge
              variant={showUnreadDot ? 'dot' : 'default'}
              content={showUnreadDot ? undefined : unreadCount}
              max={99}
              hide={!hasUnread}
              position='top-right'
              color='danger'
              size='xs'
            >
              <AIButton
                variant='soft'
                data-testid='app-header-knock-feed-button'
                size='sm'
                icon={<Icon name='message-square-warning' color='primary' />}
                aria-label={t('feed.tooltip')}
              />
            </Badge>
          </div>
        </PopoverTrigger>
      </Tooltip>

      <PopoverContent align='end' side='right' showArrow={false} className='flex w-[440px] max-w-[calc(100vw-1rem)] flex-col overflow-hidden border-gray-6 p-0 shadow-xl'>
        <div className='flex items-center justify-between gap-3 border-gray-5 border-b px-4 py-3'>
          <div>
            <Typography as='h2' variant='heading-lg'>
              {t('feed.title')}
            </Typography>
            <Typography variant='caption-xs' color='secondary'>
              {t('feed.subtitle', { count: unreadCount })}
            </Typography>
          </div>
          <Button type='button' size='sm' variant='ghost' color='primary' disabled={unreadCount === 0} onClick={() => void feedClient.markAllAsRead()}>
            {t('feed.actions.mark_all_read')}
          </Button>
        </div>

        <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as FeedTab)}>
          <TabsList variant='line' size='sm' className='w-full justify-start px-4 pt-1'>
            <TabsTrigger value='all' size='sm'>
              {t('feed.tabs.all')}
            </TabsTrigger>
            <TabsTrigger value='archived' size='sm'>
              {t('feed.tabs.archived')}
            </TabsTrigger>
          </TabsList>
        </Tabs>

        <div className='max-h-[min(560px,calc(100vh-8rem))] overflow-y-auto'>
          {isLoading && (
            <div className='flex items-center justify-center gap-2 px-4 py-10 text-gray-11'>
              <Icon name='cb-loading-spin' className='animate-spin' aria-hidden />
              <Typography variant='text-sm'>{t('feed.states.loading')}</Typography>
            </div>
          )}

          {isError && !isLoading && (
            <div className='flex flex-col items-center gap-2 px-6 py-10 text-center'>
              <Icon name='triangle-alert' className='text-danger' aria-hidden />
              <Typography variant='text-sm-medium'>{t('feed.states.error_title')}</Typography>
              <Typography variant='caption-xs' color='secondary'>
                {t('feed.states.error_description')}
              </Typography>
            </div>
          )}

          {!(isLoading || isError) && items.length === 0 && (
            <div className='flex flex-col items-center gap-2 px-6 py-10 text-center'>
              <div className='flex size-10 items-center justify-center rounded-full bg-gray-3 text-gray-10'>
                <Icon name='bell-off' aria-hidden />
              </div>
              <Typography variant='text-sm-medium'>{t('feed.states.empty_title')}</Typography>
              <Typography variant='caption-xs' color='secondary'>
                {t('feed.states.empty_description')}
              </Typography>
            </div>
          )}

          {!(isLoading || isError) &&
            items.map((item) => (
              <KnockFeedItem
                key={item.id}
                item={item}
                isArchivedView={isArchivedView}
                locale={currentLanguage}
                onArchive={(feedItem) => void feedClient.markAsArchived(feedItem)}
                onButtonAction={handleButtonAction}
                onItemClick={handleItemClick}
                onUnarchive={(feedItem) => void feedClient.markAsUnarchived(feedItem)}
              />
            ))}

          {!(isLoading || isError) && pageInfo.after && (
            <div className='flex justify-center px-4 py-3'>
              <Button type='button' size='sm' variant='outlined' color='default' loading={isFetchingMore} onClick={handleLoadMore}>
                {t('feed.actions.load_more')}
              </Button>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default KnockFeedButton;
